import { zodResolver } from '@hookform/resolvers/zod';
import {
  WireTransferPaymentMethodCreateReq,
  WireTransferPaymentMethodCreateSchema,
  WireTransferPaymentMethodCreateUI,
} from '@monetize/types/app';
import { FunctionComponent as FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useCreateWireTransferPaymentMethod } from '../../../api/accountsService';
import { parseMessageFromError } from '../../../api/axios';
import { getAccountPaymentMethodView } from '../../../constants/routes';
import { useACL } from '../../../services/acl/acl';
import { logger } from '../../../services/logger';
import { ContactSelect } from '../../Contacts/ContactSelect';
import {
  MAlert,
  MBox,
  MButton,
  MCenterModal,
  MFormField,
  MGrid,
  MGridItem,
  MStack,
  MText,
} from '../../Monetize';

interface NewWireTransferPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: string;
  paymentGatewayId: string;
}

export const NewWireTransferPaymentMethodModal: FC<
  NewWireTransferPaymentMethodModalProps
> = ({ isOpen, onClose, accountId, paymentGatewayId }) => {
  const navigate = useNavigate();
  const { canDo } = useACL();

  const [createPaymentMethodError, setCreatePaymentMethodError] =
    useState<string>('');

  const { mutateAsync: doCreateWireTransferPaymentMethod, isPending } =
    useCreateWireTransferPaymentMethod();

  const { handleSubmit, control, watch } =
    useForm<WireTransferPaymentMethodCreateUI>({
      resolver: zodResolver(WireTransferPaymentMethodCreateSchema),
      mode: 'onChange',
      defaultValues: {},
    });

  const contactId = watch('contactId');

  const onSubmit = async (requestData: WireTransferPaymentMethodCreateReq) => {
    try {
      const response = await doCreateWireTransferPaymentMethod({
        accountId,
        paymentGatewayId,
        payload: requestData,
      });

      navigate(getAccountPaymentMethodView(accountId, response.id));
    } catch (error) {
      const { summary, detail } = parseMessageFromError(error);

      setCreatePaymentMethodError(`${summary} ${detail}`);
      logger.error('Error creating wire transfer payment method:', error);
    }
  };

  const onError = (error: any) => {
    logger.error('Error submitting wire transfer payment method ', error);
  };

  const onSubmitForm = handleSubmit(onSubmit, onError);

  return (
    <form onSubmit={onSubmitForm}>
      <MCenterModal
        isOpen={isOpen}
        onClose={onClose}
        modalTitle="New Wire Transfer Payment Method"
        size="lg"
        closeOnEsc={false}
        closeOnOverlayClick={false}
        renderFooter={() => (
          <MStack
            gap={4}
            direction="row"
            align="center"
            justify="right"
            flex={1}
          >
            <MButton onClick={onClose} variant="cancel" minW="auto">
              Cancel
            </MButton>
            <MButton
              variant="primary"
              onClick={onSubmitForm}
              minW="auto"
              isDisabled={!contactId}
              isLoading={isPending}
            >
              Continue
            </MButton>
          </MStack>
        )}
      >
        <MBox>
          <MGrid templateColumns="repeat(12, 1fr)" gap={2} mb={3}>
            <MGridItem colSpan={12} key="Contact">
              <MFormField label="Contact" isRequired>
                <Controller
                  name="contactId"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <ContactSelect
                      {...field}
                      showAddContacts={canDo([['account_contacts', 'create']])}
                      accountId={accountId}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>
          </MGrid>
        </MBox>
        {createPaymentMethodError && (
          <MAlert type="error">
            <MText>{createPaymentMethodError}</MText>
          </MAlert>
        )}
      </MCenterModal>
    </form>
  );
};
